import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, orderBy, query, updateDoc, doc, where, Timestamp, getCountFromServer } from 'firebase/firestore';

type SupportStatus = 'open' | 'resolved' | 'closed';

type SupportRequestRow = {
  id: string;
  uid?: string;
  email?: string;
  message?: string;
  createdAt?: Timestamp;
  status?: SupportStatus | string;
  source?: string;
  appVersion?: string;
};

type Stats = {
  last7Days: number;
  last30Days: number;
  totalOpen: number;
};

function truncateMessage(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen) + '…';
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
}

export default function SupportRequests() {
  const [rows, setRows] = useState<SupportRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({ last7Days: 0, last30Days: 0, totalOpen: 0 });
  const [statusFilter, setStatusFilter] = useState<'all' | SupportStatus>('all');
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Support Requests — Pixano';
  }, []);

  useEffect(() => {
    const requestsQuery = query(collection(db, 'supportRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SupportRequestRow, 'id'>) }));
        setRows(data);
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);

      const [last7Snap, last30Snap, openSnap] = await Promise.all([
        getCountFromServer(
          query(collection(db, 'supportRequests'), where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo)))
        ),
        getCountFromServer(
          query(collection(db, 'supportRequests'), where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)))
        ),
        getCountFromServer(query(collection(db, 'supportRequests'), where('status', '==', 'open'))),
      ]);

      setStats({
        last7Days: last7Snap.data().count,
        last30Days: last30Snap.data().count,
        totalOpen: openSnap.data().count,
      });
    };

    loadStats().catch(() => {
      setStats({ last7Days: 0, last30Days: 0, totalOpen: 0 });
    });
  }, [rows]);

  const displayedRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((row) => (row.status || 'open') === statusFilter);
  }, [rows, statusFilter]);

  const setRowStatus = async (id: string, status: SupportStatus) => {
    setSavingId(id);
    try {
      await updateDoc(doc(db, 'supportRequests', id), { status });
    } finally {
      setSavingId(null);
    }
  };

  const statusBadgeClass = (status: string | undefined) => {
    const s = status || 'open';
    if (s === 'open') return 'bg-yellow-100 text-yellow-800';
    if (s === 'resolved') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-4 text-gray-900">Support Requests</h1>
        <p className="text-sm text-gray-600">Support messages submitted by users in the Pixano app.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded border bg-white p-4">
          <div className="text-sm text-gray-600">Last 7 Days</div>
          <div className="text-2xl font-semibold mt-2 text-gray-900">{stats.last7Days}</div>
        </div>
        <div className="rounded border bg-white p-4">
          <div className="text-sm text-gray-600">Last 30 Days</div>
          <div className="text-2xl font-semibold mt-2 text-gray-900">{stats.last30Days}</div>
        </div>
        <div className="rounded border bg-white p-4">
          <div className="text-sm text-gray-600">Total Open</div>
          <div className="text-2xl font-semibold mt-2 text-gray-900">{stats.totalOpen}</div>
        </div>
      </div>

      <div className="rounded border bg-white p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | SupportStatus)}
          className="bg-white border border-gray-300 rounded px-2 py-1 text-gray-800 max-w-xs"
        >
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="rounded border bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 border-b">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Time</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Message</th>
              <th className="px-3 py-2 text-left">App Version</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  Loading support requests...
                </td>
              </tr>
            ) : displayedRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  No support requests found.
                </td>
              </tr>
            ) : (
              displayedRows.map((row) => {
                const created = row.createdAt && typeof row.createdAt === 'object' && 'toDate' in row.createdAt
                  ? (row.createdAt as Timestamp).toDate()
                  : null;
                const msg = row.message || '';
                const shortMsg = truncateMessage(msg, 80);
                const st = (row.status || 'open') as SupportStatus;

                return (
                  <tr key={row.id} className="border-t border-gray-200 align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{created ? formatDate(created) : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{created ? formatTime(created) : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.email || '-'}</td>
                    <td className="px-3 py-2 min-w-[200px] max-w-[320px] text-gray-700" title={msg || undefined}>
                      {msg ? shortMsg : '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.appVersion || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.source || '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded px-2 py-1 text-xs ${statusBadgeClass(row.status)}`}>
                        {row.status || 'open'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex flex-wrap gap-2">
                        {st === 'open' && (
                          <>
                            <button
                              type="button"
                              className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-xs"
                              onClick={() => setRowStatus(row.id, 'resolved')}
                              disabled={savingId === row.id}
                            >
                              Resolve
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 rounded bg-gray-500 text-white hover:bg-gray-600 disabled:opacity-60 text-xs"
                              onClick={() => setRowStatus(row.id, 'closed')}
                              disabled={savingId === row.id}
                            >
                              Close
                            </button>
                          </>
                        )}
                        {st === 'resolved' && (
                          <>
                            <button
                              type="button"
                              className="px-2 py-1 rounded bg-gray-500 text-white hover:bg-gray-600 disabled:opacity-60 text-xs"
                              onClick={() => setRowStatus(row.id, 'closed')}
                              disabled={savingId === row.id}
                            >
                              Close
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-xs"
                              onClick={() => setRowStatus(row.id, 'open')}
                              disabled={savingId === row.id}
                            >
                              Reopen
                            </button>
                          </>
                        )}
                        {st === 'closed' && (
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-xs"
                            onClick={() => setRowStatus(row.id, 'open')}
                            disabled={savingId === row.id}
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
